import React, { useMemo } from 'react';
import { VictoryPie } from 'victory';
import { useTranslation } from 'react-i18next';
import styles from "./styles.module.scss";
import ChartDetails from './ChartDetails';
import { USGAAdvancedStats } from './types';

type FairwayChartProps = {
  data: USGAAdvancedStats;
};

const CustomLabel = (props: any) => {
  const { datum, x, y } = props;
  const { t } = useTranslation();
  const needsEllipse = datum.text === t('FAIRWAY HIT');

  return (
    <g transform={`translate(${x}, ${y})`}>
      <defs>
        <filter id="fairway-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.1)" />
        </filter>
      </defs>

      {needsEllipse ? (
        <>
          <rect
            x={-60}
            y={-35}
            width={120}
            height={70}
            rx={35}
            ry={35}
            fill={'#fff'}
            filter="url(#fairway-shadow)"
          />
          <text
            y="-10"
            textAnchor="middle"
            fontSize="17"
            fontWeight="700"
            fill="#000"
          >
            {`${(datum.length * 100).toFixed(0)}%`}
          </text>
          <text
            y="10"
            textAnchor="middle"
            fontSize="13"
            fontWeight="400"
            fill="#000"
          >
            {datum.text}
          </text>
        </>
      ) : (
        <>
          <circle r="35" fill='#fff' filter="url(#fairway-shadow)"/>
          <text
            y="-2"
            textAnchor="middle"
            fontSize="17"
            fontWeight="700"
            fill="#000"
          >
            {`${(datum.length * 100).toFixed(0)}%`}
          </text>
          <text
            y="15"
            textAnchor="middle"
            fontSize="13"
            fontWeight="400"
            fill="#000"
          >
            {datum.text}
          </text>
        </>
      )}
    </g>
  );
};

const FairwayChart = ({ data }: FairwayChartProps) => {
  const { t } = useTranslation();
  const interpolate = (
    value: number,
    inMin: number,
    inMax: number,
    outMin: number,
    outMax: number
  ) => {
    return outMin + ((value - inMin) * (outMax - outMin)) / (inMax - inMin);
  };

  const chartData = useMemo(() => {
    if (!data) {
      return [];
    }

    const keysToCompare = ['missedLeftPercent', 'missedRightPercent'];
    const maxKey =
      data[keysToCompare[0]] > data[keysToCompare[1]]
        ? keysToCompare[0]
        : keysToCompare[1];

    return [
      {
        length: (data.missedLeftPercent ?? 0) / 100,
        y: 60,
        color: maxKey === 'missedLeftPercent' ? '#E54500' : '#EDB9A1',
        text: t('LEFT'),
      },
      {
        length: (data.fairwayHitsPercent ?? 0) / 100,
        y: 60,
        color: '#43A047',
        text: t('FAIRWAY HIT'),
      },
      {
        length: (data.missedRightPercent ?? 0) / 100,
        y: 60,
        color: maxKey === 'missedRightPercent' ? '#E54500' : '#EDB9A1',
        text: t('RIGHT'),
      },
    ];
  }, [data, t]);

  return (
    <div className={styles.fairway_chart}>
      <svg viewBox="0 0 360 180" width={300} height={180}>
        <VictoryPie
          standalone={false}
          width={360}
          height={360}
          startAngle={-90}
          endAngle={90}
          radius={150}
          innerRadius={0}
          data={[{ y: 60 }, { y: 60 }, { y: 60 }]}
          padAngle={1}
          style={{
            data: { fill: '#F4F4F4' },
            labels: { display: 'none' },
          }}
          animate={false}
        />
        <VictoryPie
          standalone={false}
          width={360}
          height={360}
          startAngle={-90}
          endAngle={90}
          innerRadius={2}
          padAngle={1}
          labelPlacement="vertical"
          labelComponent={<CustomLabel />}
          radius={({ datum }) =>
            interpolate(Math.sqrt(datum.length * 100) / 10, 0, 1, 5, 150)
          }
          data={chartData}
          style={{
            data: { fill: ({ datum }) => datum.color },
          }}
          animate={false}
        />
      </svg>
      <ChartDetails
        data={[
          {
            title: `${data?.missedShortPercent ?? 0}%`,
            subTitle: t('Missed Short'),
          },
          {
            title: `${data?.missedLongPercent ?? 0}%`,
            subTitle: t('Missed Long'),
          },
        ]}
      />
    </div>
  );
};

export default FairwayChart;
